import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  UsersIcon,
  PhotoIcon,
  BookmarkIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  TicketIcon,
  QuestionMarkCircleIcon,
  ArrowDownTrayIcon,


  ShieldCheckIcon,
  WrenchScrewdriverIcon,
  DocumentIcon,
  XMarkIcon,
  BellAlertIcon,
} from "@heroicons/react/24/outline";
import { useUIStore } from "../../store/uiStore";
import { useAuthStore } from "../../store/authStore";
import odooClient from "../../services/odooClient";

const mainNavigation: Array<{
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  subItems?: Array<{ name: string; href: string; icon: string }>;
  superAdminOnly?: boolean;
  separator?: boolean;
}> = [
  {
    name: "Contacts",
    href: "/contacts",
    icon: UsersIcon,
    subItems: [
      { name: "Clients", href: "/contacts?type=clients", icon: "C" },
      { name: "Prospects", href: "/contacts?type=prospects", icon: "P" },
    ],
  },
  {
    name: "Artworks",
    href: "/artworks",
    icon: PhotoIcon,
  },
  {
    name: "Gallery",
    href: "/gallery",
    icon: PhotoIcon,
    separator: true,
  },
  // {
  //   name: "Interests",
  //   href: "/interests",
  //   icon: BookmarkIcon,
  // },
  {

    name: "To Do Lists",

    href: "/todos",
    icon: ClipboardDocumentListIcon,
    
  },
  {
    name: "Artists List",
    href: "/artists",
    icon: BookmarkIcon,
  },
  {
    name: "New List",
    href: "/artlist",
    icon: ClipboardDocumentListIcon,
    separator: true,
  },
  {
    name: "Manage List",
    href: "/artlist-manage",
    icon: ClipboardDocumentListIcon,
  },
  {
    name: "Send List",
    href: "/artwork-pdf-generator",
    icon: DocumentIcon,
  },
  // {
  //   name: "Offers",
  //   href: "/offers",
  //   icon: DocumentIcon,
  //   separator: true,
  // },
  // {
  //   name: "2Fa",
  //   href: "/2fa-settings",
  //   icon: ClipboardDocumentListIcon,
  // },
];

const bottomNavigation = [
  { name: "Proposal", href: "/proposals", icon: DocumentTextIcon },
  { name: "Tickets", href: "/tickets", icon: TicketIcon },
  { name: "Help", href: "/help", icon: QuestionMarkCircleIcon },
  { name: "Import", href: "/import", icon: ArrowDownTrayIcon },
];

type SidebarProps = {
  variant?: "drawer" | "persistent";
};

export default function Sidebar({ variant = "persistent" }: SidebarProps) {
  const isDrawer = variant === "drawer";
  const location = useLocation();
  const navigate = useNavigate();
  const closeSidebar = useUIStore((s) => s.closeSidebar);
  const { user } = useAuthStore();
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);

  const isSuperAdmin = useMemo(() => {
    const groups = (user?.permissions || []).map((g) =>
      String(g || "").toLowerCase(),
    );
    if (groups.includes("artgallery_custom_fields.group_artgallery_superuser"))
      return true;
    return groups.some((s) => s.includes("group_artgallery_superuser"));
  }, [user?.permissions]);

  const isCommunicationService = useMemo(() => {
    const groups = (user?.permissions || []).map((g) =>
      String(g || "").toLowerCase(),
    );
    if (
      groups.includes(
        "artgallery_custom_fields.group_artgallery_communication_service",
      )
    )
      return true;
    return groups.some((s) =>
      s.includes("group_artgallery_communication_service"),
    );
  }, [user?.permissions]);

  // Check if current path matches contacts or its sub-items
  const isContactsActive =
    location.pathname === "/contacts" ||
    location.search.includes("type=clients") ||
    location.search.includes("type=prospects");

  const isAdminActive =
    location.pathname === "/artworks-no-image" ||
    location.pathname === "/admin-dashboard" ||
    location.pathname === "/dashboard-notification" ||
    location.pathname === "/artworks-low-price" ||
    location.pathname === "/contacts-no-phone-or-email";

  const toggleMaintenance = async () => {
    if (maintenanceLoading) return;

    try {
      setMaintenanceLoading(true);

      const backendUrl =
                (odooClient as any)["backendURL"] ||
                process.env.REACT_APP_BACKEND_URL ||
                "";

      await fetch(`${backendUrl}/api/admin/maintenance`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ enabled: !maintenanceEnabled }),
      });

      setMaintenanceEnabled(!maintenanceEnabled);
    } catch (err) {
      alert("Failed to update maintenance mode");
    } finally {
      setMaintenanceLoading(false);
    }
  };

  useEffect(() => {
  if (!isSuperAdmin) return;

  const backendUrl =
    (odooClient as any)["backendURL"] ||
    process.env.REACT_APP_BACKEND_URL ||
    "";

  fetch(`${backendUrl}/api/admin/maintenance`)
    .then((r) => r.json())
    .then((d) => {
      setMaintenanceEnabled(Boolean(d?.maintenance));
    })
    .catch(() => {});
}, [isSuperAdmin]);


  return (
    <div className="flex h-full w-full flex-col overflow-auto border-r border-[#2a2a2a] bg-[#111111] lg:w-52">
      {/* Logo */}
      <div className="flex items-center justify-between border-b border-[#2a2a2a] px-4 py-4 sm:py-6">
        <button
          type="button"
          onClick={() => {
            navigate("/dashboard");
            closeSidebar();
          }}
          className="truncate text-base font-bold tracking-wider text-[#c9a84c] transition-colors hover:text-[#e0bc5c] sm:text-lg"
        >
          ART GALLERY
        </button>
        {isDrawer && (
          <button
            type="button"
            onClick={closeSidebar}
            className="ml-2 shrink-0 rounded-lg p-1.5 text-[#999999] hover:bg-[#1a1a1a] hover:text-[#c9a84c]"
            aria-label="Close menu"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {mainNavigation
          .filter((item) => {
            if (item.superAdminOnly && !isSuperAdmin) return false;

            if (item.name === "Contacts" && isCommunicationService)
              return false;
            return true;
          })
          .map((item) => (
            <div key={item.name}>
              {/* Separator line before items that have separator: true */}
              {item.separator && (
                <hr className="border-t border-[#2a2a2a] my-2" />
              )}

              <NavLink
                to={item.href}
                onClick={closeSidebar}
                className={({ isActive }) => {
                  const isItemActive =
                    isActive ||
                    (item.name === "Contacts" && isContactsActive) ||
                    (item.name === "Admin" && isAdminActive);
                  return `flex items-center px-3 py-3 text-sm font-medium transition-colors border-l-2 ${
                    isItemActive
                      ? "text-[#c9a84c] bg-[#161310] border-[#c9a84c]"
                      : "text-[#999999] border-transparent hover:text-[#c9a84c] hover:bg-[#161310]"
                  }`;
                }}
              >
                {({ isActive }) => {
                  const isItemActive =
                    isActive ||
                    (item.name === "Contacts" && isContactsActive) ||
                    (item.name === "Admin" && isAdminActive);
                  return (
                    <>
                      <div
                        className={`w-8 h-8 mr-3 flex items-center justify-center rounded-full ${
                          isItemActive ? "bg-[#c9a84c]" : "bg-[#2a2418]"
                        }`}
                      >
                        <item.icon
                          className={`w-5 h-5 ${
                            isItemActive ? "text-black" : "text-[#c9a84c]"
                          }`}
                        />
                      </div>
                      <span>{item.name}</span>
                    </>
                  );
                }}
              </NavLink>

              {/* Sub Navigation for Contacts - Always show if it has subitems */}
              {item.subItems && (
                <div className="relative ml-6 mt-1 space-y-1">
                  {/* Connection line - only spans the sub-items */}
                  <div
                    className="absolute left-2 top-0 w-px bg-[#2a2a2a]"
                    style={{ height: `${item.subItems.length * 2}rem` }}
                  ></div>

                  {item.subItems.map((subItem, index) => {
                    const isSubItemActive =
                      location.pathname + location.search === subItem.href;
                    return (
                      <div key={subItem.name} className="relative">
                        {/* Horizontal line */}
                        <div className="absolute left-2 top-4 w-4 h-px bg-[#2a2a2a]"></div>

                        <NavLink
                          to={subItem.href}
                          onClick={closeSidebar}
                          className={`flex items-center pl-6 pr-3 py-2 text-sm transition-colors ${
                            isSubItemActive
                              ? "text-[#c9a84c]"
                              : "text-[#777777] hover:text-[#c9a84c]"
                          }`}
                        >
                          <div className="w-6 h-6 mr-2 flex items-center justify-center">
                            <span
                              className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium ${
                                isSubItemActive
                                  ? "bg-[#2a2418] text-[#c9a84c]"
                                  : "bg-[#1a1a1a] text-[#999999]"
                              }`}
                            >
                              {subItem.icon}
                            </span>
                          </div>
                          <span>{subItem.name}</span>
                        </NavLink>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
      </nav>

      {/* Bottom External Links */}
      <nav className="px-3 py-4 border-t border-[#2a2a2a] space-y-1">
        {isSuperAdmin && (
          <div>
            <NavLink
              to="/artworks-no-image"
              onClick={closeSidebar}
              className={({ isActive }) => {
                const isItemActive = isAdminActive;
                return `flex items-center px-3 py-3 text-sm font-medium transition-colors border-l-2 ${
                  isItemActive
                    ? "text-[#c9a84c] bg-[#161310] border-[#c9a84c]"
                    : "text-[#999999] border-transparent hover:text-[#c9a84c] hover:bg-[#161310]"
                }`;
              }}
            >
              {({ isActive }) => {
                const isItemActive = isAdminActive;
                return (
                  <>
                    <div
                      className={`w-8 h-8 mr-3 flex items-center justify-center rounded-full ${
                        isItemActive ? "bg-[#c9a84c]" : "bg-[#2a2418]"
                      }`}
                    >
                      <ShieldCheckIcon
                        className={`w-5 h-5 ${
                          isItemActive ? "text-black" : "text-[#c9a84c]"
                        }`}
                      />
                    </div>
                    <span>Admin</span>
                  </>
                );
              }}
            </NavLink>

            {isAdminActive && (
              <div className="relative ml-6 mt-1 space-y-1">
                <div
                  className="absolute left-2 top-0 w-px bg-[#2a2a2a]"
                  style={{ height: "10rem" }}
                ></div>

                <div className="relative">
                  <div className="absolute left-2 top-4 w-4 h-px bg-[#2a2a2a]"></div>

                  <NavLink
                    to="/artworks-no-image"
                    onClick={closeSidebar}
                    className={({ isActive }) =>
                      `flex items-center pl-6 pr-3 py-2 text-sm transition-colors ${
                        isActive
                          ? "text-[#c9a84c]"
                          : "text-[#777777] hover:text-[#c9a84c]"
                      }`
                    }
                  >
                    <div className="w-6 h-6 mr-2 flex items-center justify-center">
                      <span
                        className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium ${
                          location.pathname === "/artworks-no-image"
                            ? "bg-[#2a2418] text-[#c9a84c]"
                            : "bg-[#1a1a1a] text-[#999999]"
                        }`}
                      >
                        ⚠
                      </span>
                    </div>
                    <span>Artworks - No Image</span>
                  </NavLink>
                </div>

                <div className="relative">
                  <div className="absolute left-2 top-4 w-4 h-px bg-[#2a2a2a]"></div>

                  <NavLink
                    to="/dashboard-notification"
                    onClick={closeSidebar}
                    className={({ isActive }) =>
                      `flex items-center pl-6 pr-3 py-2 text-sm transition-colors ${
                        isActive
                          ? "text-[#c9a84c]"
                          : "text-[#777777] hover:text-[#c9a84c]"
                      }`
                    }
                  >
                    <div className="w-6 h-6 mr-2 flex items-center justify-center">
                      <BellAlertIcon
                        className={`w-5 h-5 ${
                          location.pathname === "/dashboard-notification"
                            ? "text-[#c9a84c]"
                            : "text-[#777777]"
                        }`}
                      />
                    </div>
                    <span>Dashboard Notification</span>
                  </NavLink>
                </div>

                <div className="relative">
                  <div className="absolute left-2 top-4 w-4 h-px bg-[#2a2a2a]"></div>

                  <NavLink
                    to="/artworks-low-price"
                    onClick={closeSidebar}
                    className={({ isActive }) =>
                      `flex items-center pl-6 pr-3 py-2 text-sm transition-colors ${
                        isActive
                          ? "text-[#c9a84c]"
                          : "text-[#777777] hover:text-[#c9a84c]"
                      }`
                    }
                  >
                    <div className="w-6 h-6 mr-2 flex items-center justify-center">
                      <span
                        className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium ${
                          location.pathname === "/artworks-low-price"
                            ? "bg-[#2a2418] text-[#c9a84c]"
                            : "bg-[#1a1a1a] text-[#999999]"
                        }`}
                      >
                        ⚠
                      </span>
                    </div>

                    <span>Artworks - No Price</span>
                  </NavLink>
                </div>

                <div className="relative">
                  <div className="absolute left-2 top-4 w-4 h-px bg-[#2a2a2a]"></div>

                  <NavLink
                    to="/admin-dashboard"
                    onClick={closeSidebar}
                    className={({ isActive }) =>
                      `flex items-center pl-6 pr-3 py-2 text-sm transition-colors ${
                        isActive
                          ? "text-[#c9a84c]"
                          : "text-[#777777] hover:text-[#c9a84c]"
                      }`
                    }
                  >
                    <div className="w-6 h-6 mr-2 flex items-center justify-center">
                      <span
                        className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium ${
                          location.pathname === "/admin-dashboard"
                            ? "bg-[#2a2418] text-[#c9a84c]"
                            : "bg-[#1a1a1a] text-[#999999]"
                        }`}
                      >
                        ⚠
                      </span>
                    </div>
                    <span>Activity Log</span>
                  </NavLink>
                </div>

                <div className="relative">
                  <div className="absolute left-2 top-4 w-4 h-px bg-[#2a2a2a]"></div>

                  <NavLink
                    to="/contacts-no-phone-or-email"
                    onClick={closeSidebar}
                    className={({ isActive }) =>
                      `flex items-center pl-6 pr-3 py-2 text-sm transition-colors ${
                        isActive
                          ? "text-[#c9a84c]"
                          : "text-[#777777] hover:text-[#c9a84c]"
                      }`
                    }
                  >
                    <div className="w-6 h-6 mr-2 flex items-center justify-center">
                      <span
                        className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium ${
                          location.pathname === "/contacts-no-phone-or-email"
                            ? "bg-[#2a2418] text-[#c9a84c]"
                            : "bg-[#1a1a1a] text-[#999999]"
                        }`}
                      >
                        ⚠
                      </span>
                    </div>
                    <span>Contacts - No Contact Information</span>
                  </NavLink>
                </div>
              </div>
            )}
          </div>
        )}

        <a
          href="https://help.operacrm.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center px-3 py-3 text-sm font-medium transition-colors text-[#999999] hover:text-[#c9a84c]"
        >
          <div className="w-8 h-8 mr-3 flex items-center justify-center rounded-full bg-[#2a2418]">
            <TicketIcon className="w-5 h-5 text-[#c9a84c]" />
          </div>
          <span>Tickets</span>
        </a>
        <a
          href="https://bit.ly/opercrmguide"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center px-3 py-3 text-sm font-medium transition-colors text-[#999999] hover:text-[#c9a84c]"
        >
          <div className="w-8 h-8 mr-3 flex items-center justify-center rounded-full bg-[#2a2418]">
            <QuestionMarkCircleIcon className="w-5 h-5 text-[#c9a84c]" />
          </div>
          <span>Help</span>
        </a>

        {isSuperAdmin && (
          <div className="mt-2">
            <button
              onClick={toggleMaintenance}
              disabled={maintenanceLoading}
              className={`flex w-full items-center px-3 py-3 text-sm font-medium transition-colors
        ${
          maintenanceEnabled
            ? "text-red-400"
            : "text-[#999999] hover:text-[#c9a84c]"
        }
        ${maintenanceLoading ? "opacity-50 cursor-not-allowed" : ""}
      `}
            >
              <div
                className={`w-8 h-8 mr-3 flex items-center justify-center rounded-full
          ${maintenanceEnabled ? "bg-red-600" : "bg-[#2a2418]"}
        `}
              >
                <WrenchScrewdriverIcon
                  className={`w-5 h-5 ${
                    maintenanceEnabled ? "text-white" : "text-[#c9a84c]"
                  }`}
                />
              </div>

              <span className="text-sm">
                {maintenanceEnabled ? "Off Maintenance" : "On Maintenance"}
              </span>
            </button>
          </div>
        )}

        <span className="ml-11 text-xs text-[#555555]">
          Build Version: {process.env.REACT_APP_BUILD_VERSION || "dev"}
        </span>
      </nav>
    </div>
  );
}